import { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import { FaPlay, FaXmark, FaFilm } from "react-icons/fa6";
import { useHomeContext } from "../../../context/Home_context";
import { getEmbedUrl, getYouTubeThumbnail } from "../../../utils/helper";
import DOMPurify from "dompurify";
import "./CurtainRaiserVideoPage.css";

Modal.setAppElement("#root");

function useReveal() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.18 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

function VideoCard({ video, index, onPlayVideo }) {
  const [ref, inView] = useReveal();
  const thumbnail = video.thumbnail || getYouTubeThumbnail(video.videoUrl);

  return (
    <article
      ref={ref}
      className={`vb-card ${inView ? "vb-in-view" : ""}`}
      style={{ "--delay": `${(index % 3) * 90}ms` }}
      onClick={() => onPlayVideo(video)}
    >
      <div className="vb-card-media">
        <img
          src={thumbnail}
          alt={video.title}
          className="vb-card-thumb-img"
          loading="lazy"
        />
        <button
          className="vb-play"
          aria-label={`Play: ${video.title}`}
          onClick={(e) => {
            e.stopPropagation();
            onPlayVideo(video);
          }}
        >
          <span className="vb-play-ring" />
          <span className="vb-play-ring vb-play-ring--2" />
          <FaPlay size={20} style={{ marginLeft: "2px" }} />
        </button>
        <div className="vb-card-shine" />
      </div>
      <div className="vb-card-body">
        <h3>{video.title}</h3>
      </div>
    </article>
  );
}

const CurtainRaiserVideo = () => {
  const {
    current_expo_data,
    curtain_raiser_category_data,
    curtain_raiser_data,
    GetCurtainRaiserCategory,
    GetCurtainRaiser,
  } = useHomeContext();

  const curtainRaiserDesc =
    current_expo_data?.curtain_Raiser_description ||
    (typeof curtain_raiser_data === "object" &&
      !Array.isArray(curtain_raiser_data)
      ? curtain_raiser_data?.curtain_Raiser_description
      : "");

  const [activeEdition, setActiveEdition] = useState(null);
  const [activeModalVideo, setActiveModalVideo] = useState(null);

  useEffect(() => {
    if (GetCurtainRaiserCategory) {
      GetCurtainRaiserCategory();
    }
  }, []);

  const editionTabs = useMemo(() => {
    if (
      curtain_raiser_category_data &&
      Array.isArray(curtain_raiser_category_data) &&
      curtain_raiser_category_data.length > 0
    ) {
      return curtain_raiser_category_data.map((cat) => ({
        id: cat.id,
        label: cat.name,
      }));
    }
    return [];
  }, [curtain_raiser_category_data]);

  useEffect(() => {
    if (
      editionTabs.length > 0 &&
      (activeEdition === null || typeof activeEdition === "string")
    ) {
      const firstId = editionTabs[0].id;
      setActiveEdition(firstId);
      if (GetCurtainRaiser) {
        GetCurtainRaiser(firstId);
      }
    }
  }, [editionTabs]);

  const handleEditionTabClick = (tabId) => {
    setActiveEdition(tabId);
    if (GetCurtainRaiser) {
      GetCurtainRaiser(tabId);
    }
  };

  const activeVideos = useMemo(() => {
    let rawItems = [];

    const extractItems = (node) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach((item) => extractItems(item));
      } else if (typeof node === "object") {
        if (Array.isArray(node.childs) && node.childs.length > 0) {
          node.childs.forEach((c) => extractItems(c));
        } else if (Array.isArray(node.data) && node.data.length > 0) {
          node.data.forEach((d) => extractItems(d));
        } else if (
          node.video_link ||
          node.name ||
          node.description ||
          node.curtain_Raiser_description
        ) {
          rawItems.push(node);
        }
      }
    };

    extractItems(curtain_raiser_data);

    if (rawItems.length > 0) {
      return rawItems.map((item, index) => ({
        id: item.id || index,
        title:
          item.curtain_Raiser_description ||
          item.curtain_raiser_description ||
          item.description ||
          item.title ||
          item.name ||
          "",
        speaker: item.name || item.speaker || "",
        role: item.designation || item.role || "",
        thumbnail:
          item.image_full_path ||
          item.thumbnail_full_path ||
          item.thumbnail ||
          getYouTubeThumbnail(
            item.video_link || item.video_url || item.videoUrl,
          ),
        videoUrl:
          item.video_link || item.video_url || item.videoUrl || item.link || "",
      }));
    }

    return [];
  }, [curtain_raiser_data]);

  useEffect(() => {
    if (activeModalVideo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeModalVideo]);

  return (
    <section className="vb-page">
      <div className="vb-bg-orb vb-bg-orb--1" />
      <div className="vb-bg-orb vb-bg-orb--2" />
      <div className="vb-bg-orb vb-bg-orb--3" />
      <div className="vb-bg-grid" />

      <header className="vb-hero">
        <div className="clean-breadcrumb-wrapper">
          <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="bc-home-link">
              HOME
            </Link>
            <span className="bc-slash">/</span>
            <span className="bc-active-page">VIDEOS</span>
          </nav>
        </div>
        <h1 className="vb-title">
          <span> Curtain Raiser </span>
        </h1>
        {curtainRaiserDesc && (
          <div
            className="vb-subtitle"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(curtainRaiserDesc),
            }}
          />
        )}
      </header>

      {editionTabs.length > 0 && (
        <div className="vb-tabs-shell">
          <div className="vb-tabs-track">
            {editionTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`vb-tab-btn ${activeEdition === tab.id ? "active" : ""}`}
                onClick={() => handleEditionTabClick(tab.id)}
              >
                <span className="vb-tab-dot" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeVideos.length === 0 ? (
        <div className="vb-no-videos-wrapper">
          <FaFilm className="vb-no-videos-big-icon" />
          <p className="vb-no-videos-simple-text">
            No Videos Available in this Category
          </p>
        </div>
      ) : (
        <div className="vb-grid">
          {activeVideos.map((video, i) => (
            <VideoCard
              key={video.id || i}
              video={video}
              index={i}
              onPlayVideo={(v) => setActiveModalVideo(v)}
            />
          ))}
        </div>
      )}

      {/* react-modal Lightbox Player */}
      <Modal
        isOpen={Boolean(activeModalVideo)}
        onRequestClose={() => setActiveModalVideo(null)}
        className="vb-modal-dialog"
        overlayClassName="vb-modal-backdrop"
        contentLabel={activeModalVideo?.title || "Video Player"}
        closeTimeoutMS={200}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        {activeModalVideo && (
          <div className="vb-modal-content">
            <div className="vb-modal-header">
              <h4 className="vb-modal-heading">{activeModalVideo.title}</h4>
              <button
                type="button"
                className="vb-modal-close-btn"
                onClick={() => setActiveModalVideo(null)}
                aria-label="Close"
              >
                <FaXmark size={16} />
              </button>
            </div>
            <div className="vb-modal-player-wrap">
              <iframe
                className="vb-modal-iframe"
                src={getEmbedUrl(activeModalVideo.videoUrl)}
                title={activeModalVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default CurtainRaiserVideo;
